import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";
import { readApiCode, readApiMessages, readApiStatus } from "@/shared/utils/apiError";

export type CryptoSwapErrorKind =
  /** 401/403 on a read. The surface latches and renders the no-access view. */
  | "access_denied"
  /** The organization has not completed its own verification. */
  | "verification_required"
  /** A 4xx the backend explained in the user's language (insufficient funds, limits, bad address…). */
  | "rejected"
  /** The price lapsed before the order reached the server. Re-quote with a new idempotency key. */
  | "quote_expired"
  /** 409 on create: the quote was already redeemed, or the same key is still in flight. */
  | "conflict"
  /** No answer, or a 5xx, on create: the order may exist. Never offer a blind retry. */
  | "unconfirmed"
  | "not_found"
  | "unavailable"
  | "network"
  | "unknown";

export interface NormalizedCryptoSwapError {
  kind: CryptoSwapErrorKind;
  status: number | null;
  code: string | null;
  messageKey: keyof CryptoSwapsTranslations;
  /** The backend's own sentences. Localized server-side through `Accept-Language`, so they render as-is. */
  serverMessages: string[];
  retryable: boolean;
}

export const AMOUNT_BELOW_MINIMUM_CODE = "amount_below_minimum";
export const AMOUNT_ABOVE_MAXIMUM_CODE = "amount_above_maximum";
export const AMOUNT_OUT_OF_RANGE_CODE = "amount_out_of_range";

const RANGE_CODES: readonly string[] = [
  AMOUNT_BELOW_MINIMUM_CODE,
  AMOUNT_ABOVE_MAXIMUM_CODE,
  AMOUNT_OUT_OF_RANGE_CODE,
];

/** A pricing refusal of the amount against the per-purchase limits: it belongs on the field, not a banner. */
export function isRangeRejection(error: NormalizedCryptoSwapError | null | undefined): boolean {
  return (
    !!error && error.kind === "rejected" && error.status === 400 && RANGE_CODES.includes(error.code ?? "")
  );
}

export type CryptoSwapEndpoint = "pairs" | "quotes" | "orders_create" | "orders_read";

export interface NormalizeOptions {
  /** Create only: the quote's own expiry had passed when the request failed. */
  quoteExpired?: boolean;
}

const VERIFICATION_REQUIRED_CODE = "organization_verification_required";
const BREB_KEY_REQUIRED_CODE = "breb_key_required";
export const OTC_NOT_ENABLED_CODE = "otc_not_enabled";

export function normalizeCryptoSwapError(
  error: unknown,
  endpoint: CryptoSwapEndpoint,
  options: NormalizeOptions = {},
): NormalizedCryptoSwapError {
  const status = readApiStatus(error);
  const code = readApiCode(error);
  const serverMessages = readApiMessages(error);
  const isCreate = endpoint === "orders_create";
  const base = { status, code, serverMessages };

  if (status === null) {
    return isCreate
      ? { ...base, kind: "unconfirmed", messageKey: "error_unconfirmed", retryable: false }
      : { ...base, kind: "network", messageKey: "error_network", retryable: true };
  }

  if (status >= 500) {
    if (isCreate) return { ...base, kind: "unconfirmed", messageKey: "error_unconfirmed", retryable: false };
    return {
      ...base,
      kind: "unavailable",
      messageKey: endpoint === "quotes" ? "error_price_unavailable" : "error_service_unavailable",
      retryable: true,
    };
  }

  if (code === VERIFICATION_REQUIRED_CODE) {
    return {
      ...base,
      kind: "verification_required",
      messageKey: "error_verification_required",
      retryable: false,
    };
  }

  if (code === BREB_KEY_REQUIRED_CODE) {
    return { ...base, kind: "rejected", messageKey: "error_breb_key_required", retryable: false };
  }

  // A product switch, not a permission: it must not latch the surface into the no-access view.
  if (code === OTC_NOT_ENABLED_CODE) {
    return { ...base, kind: "rejected", messageKey: "error_otc_not_enabled", retryable: false };
  }

  if (status === 401 || status === 403) {
    return isCreate || endpoint === "quotes"
      ? { ...base, kind: "rejected", messageKey: "error_forbidden", retryable: false }
      : { ...base, kind: "access_denied", messageKey: "error_forbidden", retryable: false };
  }

  if (status === 409 && isCreate) {
    return { ...base, kind: "conflict", messageKey: "error_conflict", retryable: false };
  }

  if (status === 404) {
    return { ...base, kind: "not_found", messageKey: "error_not_found", retryable: false };
  }

  if (status === 400 && isCreate && options.quoteExpired) {
    return { ...base, kind: "quote_expired", messageKey: "error_quote_expired", retryable: false };
  }

  if (status >= 400 && status < 500) {
    return { ...base, kind: "rejected", messageKey: "error_rejected", retryable: false };
  }

  return { ...base, kind: "unknown", messageKey: "error_unknown", retryable: true };
}

/** The price lapsed while the order was being prepared (a second factor, say): nothing was sent. */
export const QUOTE_LAPSED_ERROR: NormalizedCryptoSwapError = {
  kind: "quote_expired",
  status: null,
  code: null,
  messageKey: "error_quote_expired",
  serverMessages: [],
  retryable: false,
};

/** The sentence to render: the backend's own when it sent one for a 4xx, else the fallback key. */
export function cryptoSwapErrorText(
  error: NormalizedCryptoSwapError,
  translate: (key: keyof CryptoSwapsTranslations) => string,
): string {
  const useServer =
    error.serverMessages.length > 0 &&
    error.status !== null &&
    error.status >= 400 &&
    error.status < 500 &&
    error.kind !== "access_denied";
  return useServer ? error.serverMessages.join(" ") : translate(error.messageKey);
}
